/**
 * DelveModal.js — The Delve dungeon UI (v348)
 *
 * Appears when the player steps on a delve entrance tile.
 * Manages the full run lifecycle: entry → map → node resolution → boss → rewards.
 *
 * Props:
 *   character        — current character object
 *   socket           — socket.io socket
 *   currentBattle    — current battle state (from GamePage)
 *   onBattleStart    — called when delve combat is initiated (unused now but kept for compatibility)
 *   onClose          — called to dismiss the modal
 *   onCharacterUpdate — called after gold/inventory changes
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './DelveModal.css';
import { formatCurrency } from './shared/formatCompact';

const API = process.env.REACT_APP_SERVER_URL || '';

// ─────────────────────────────────────────────────
// Node type helpers
// ─────────────────────────────────────────────────
const NODE_ICON = {
  map:    '⚔️',
  event:  '📜',
  chest:  '💰',
  rest:   '🔥',
  shrine: '⛩️',
  boss:   '💀',
};
const NODE_LABEL = {
  map:    'Combat',
  event:  'Event',
  chest:  'Chest',
  rest:   'Rest',
  shrine: 'Shrine',
  boss:   'Boss',
};
const NODE_COLOR = {
  map:    '#c0392b',
  event:  '#8e44ad',
  chest:  '#d4ac0d',
  rest:   '#27ae60',
  shrine: '#e67e22',
  boss:   '#e74c3c',
};

// ─────────────────────────────────────────────────
// DelveModal
// ─────────────────────────────────────────────────
const DelveModal = ({ character, socket, currentBattle, onClose, onCharacterUpdate, onOpenGuide }) => {
  const token = localStorage.getItem('token');

  // ── State ──────────────────────────────────────
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [run, setRun]                 = useState(null);
  const [modifier, setModifier]       = useState(null);
  const [entry, setEntry]             = useState(null);
  const [tokenCost, setTokenCost]     = useState(null);
  const [screen, setScreen]           = useState('loading'); // see SCREEN VALUES below
  const [nodeContent, setNodeContent] = useState(null);      // activeNodeContent from enter-node
  const [result, setResult]           = useState(null);      // resolution/rewards data
  const [actionLoading, setActionLoading] = useState(false);
  const [runSummary, setRunSummary]   = useState(null);      // for abandon/complete summaries
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const [streak, setStreak]           = useState(0);
  const [streakTitle, setStreakTitle]  = useState(null);

  // Combat tracking
  const prevBattleRef   = useRef(null);
  const combatNodeRef   = useRef(null); // nodeId that triggered combat
  const bossClaimInFlightRef = useRef(false);

  // ── API helper ─────────────────────────────────
  const apiFetch = useCallback(async (path, method = 'GET', body = null) => {
    const opts = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${token}`,
      },
    };
    if (body) opts.body = JSON.stringify(body);
    const res  = await fetch(`${API}/api/delve${path}`, opts);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  }, [token]);

  const finalizeBossVictory = useCallback(async () => {
    if (bossClaimInFlightRef.current) return null;
    bossClaimInFlightRef.current = true;
    try {
      const data = await apiFetch('/claim-boss-rewards', 'POST');
      // If the run was already cleared (death/flee/duplicate claim), go to entry
      if (data.alreadyClaimed) {
        setRun(null);
        setModifier(null);
        setNodeContent(null);
        setScreen('entry');
        if (onCharacterUpdate) onCharacterUpdate();
        return data;
      }
      setRun(null);
      setModifier(null);
      setNodeContent(null);
      setRunSummary({ victory: true, ...data });
      setScreen('run_complete');
      if (onCharacterUpdate) onCharacterUpdate();
      return data;
    } finally {
      bossClaimInFlightRef.current = false;
      combatNodeRef.current = null;
    }
  }, [apiFetch, onCharacterUpdate]);

  // ── Fetch run state ────────────────────────────
  const fetchState = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetch('/state');
      setRun(data.run);
      setModifier(data.modifier);
      setEntry(data.entry);
      setTokenCost(data.tokenCost ?? null);
      setStreak(data.streak || 0);
      setStreakTitle(data.streakTitle || null);

      if (data.run && data.run.status === 'active') {
        if (
          data.battleState === 'none' &&
          data.run.activeNodeContent &&
          data.run.activeNodeContent.type === 'boss' &&
          data.run.activeNodeContent.bossDefeated
        ) {
          try {
            await finalizeBossVictory();
            return;
          } catch (claimErr) {
            // Boss finalization failed (network, already claimed, etc.)
            // Fall back to entry screen so the player isn't stuck in boss chamber.
            console.warn('[Delve] Boss finalization failed during recovery:', claimErr.message || claimErr);
            setScreen('entry');
            return;
          }
        }

        // Resume a run
        if (data.run.activeNodeContent) {
          setNodeContent(data.run.activeNodeContent);
          setScreen(_screenForContent(data.run.activeNodeContent));
        } else {
          setScreen('map');
        }
      } else {
        setScreen('entry');
      }
    } catch (err) {
      setError(err.message);
      setScreen('entry');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, finalizeBossVictory]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  // ── Detect battle completion ────────────────────
  useEffect(() => {
    const wasBattling = !!prevBattleRef.current;
    const isBattling  = !!currentBattle;

    if (wasBattling && !isBattling) {
      // Battle just ended — complete the delve node if it was a combat
      if (combatNodeRef.current) {
        const nodeType = combatNodeRef.current.type;
        // v419: Clear ref immediately to prevent duplicate calls on re-render
        combatNodeRef.current = null;
        if (nodeType === 'map') {
          // Call complete-node, then return to map
          (async () => {
            try {
              const data = await apiFetch('/complete-node', 'POST');
              setRun(data.run);
              setNodeContent(null);
              setResult({
                type: 'combat_complete',
                message: data.message || 'Victory! You press deeper into the Delve.',
                milestone: data.milestone,
              });
              setScreen('combat_result');
              if (onCharacterUpdate) onCharacterUpdate();
            } catch (err) {
              // Might fail if already completed; just return to map
              await fetchState();
            }
          })();
        } else if (nodeType === 'boss') {
          // Call claim-boss-rewards
          (async () => {
            try {
              await finalizeBossVictory();
            } catch (err) {
              // Already claimed or failed; refetch
              await fetchState();
            }
          })();
        }
      }
    }

    prevBattleRef.current = currentBattle;
  }, [currentBattle, fetchState, finalizeBossVictory]);

  // ── Buy Delve Token ────────────────────────────
  const handleBuyToken = useCallback(async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await apiFetch('/buy-token', 'POST');
      onCharacterUpdate();
      await fetchState(); // re-check entry eligibility
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  }, [actionLoading, apiFetch, fetchState, onCharacterUpdate]);

  // ── Screen dispatcher ──────────────────────────────────
  function _screenForContent(content) {
    if (!content) return 'map';
    switch (content.type) {
      case 'map':   return 'node_combat';
      case 'event': {
        // Event that already triggered card choices goes straight to card pick
        if (content.cardChoices && content.cardChoices.length > 0) return 'node_card_pick';
        return 'node_event';
      }
      case 'chest': return 'node_chest';
      case 'rest':  return 'node_rest';
      case 'shrine': return 'node_shrine';
      case 'boss':  return 'node_boss';
      default:      return 'map';
    }
  }

  // ── Actions ────────────────────────────────────

  const handleStart = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/start', 'POST');
      setRun(data.run);
      setModifier(data.modifier);
      setNodeContent(null);
      setResult(null);
      setScreen('map');
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEnterNode = async (nodeId) => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/enter-node', 'POST', { nodeId });
      setRun(data.run);
      setNodeContent(data.nodeContent);
      setResult(null);
      setScreen(_screenForContent(data.nodeContent));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveEvent = async (eventId, optionId) => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/resolve-event', 'POST', { eventId, optionId });
      setRun(data.run);
      if (data.resolution.cardChoices && data.resolution.cardChoices.length > 0) {
        setNodeContent({ ...nodeContent, cardChoices: data.resolution.cardChoices });
        setScreen('node_card_pick');
      } else {
        setResult(data.resolution);
        setNodeContent(null);
        setScreen('event_result');
      }
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenChest = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/open-chest', 'POST');
      setRun(data.run);
      // Merge rewards into nodeContent so ScreenChest can display them
      setNodeContent({ ...data.run.activeNodeContent });
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClaimChestLoot = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/claim-chest-loot', 'POST');
      setRun(data.run);
      setNodeContent({ ...data.run.activeNodeContent });
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseChest = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/close-chest', 'POST');
      setRun(data.run);
      setNodeContent(null);
      setScreen('map');
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePickCard = async (cardName) => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/pick-card', 'POST', { cardName });
      setRun(data.run);
      if (data.run.activeNodeContent) {
        // Still in a chest — card claimed, update nodeContent and stay on chest screen
        setNodeContent({ ...data.run.activeNodeContent });
        if (onCharacterUpdate) onCharacterUpdate();
      } else {
        // Event or other non-chest source — go to result screen
        setResult({ cardPicked: data.selectedCard, message: data.message });
        setNodeContent(null);
        setScreen('card_picked');
        if (onCharacterUpdate) onCharacterUpdate();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipCard = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/skip-card', 'POST');
      setRun(data.run);
      setNodeContent(null);
      setResult(null);
      setScreen('map');
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRest = async (option) => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/rest', 'POST', { option });
      setRun(data.run);
      setResult({ message: data.message });
      setNodeContent(null);
      setScreen('rest_result');
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveShrine = async (offerId) => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/resolve-shrine', 'POST', { offerId });
      setRun(data.run);
      if (data.newModifier) setModifier(data.newModifier);
      setResult({ message: data.message, newModifier: data.newModifier });
      setNodeContent(null);
      setScreen('shrine_result');
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartCombat = async () => {
    try {
      setActionLoading(true);
      setError(null);
      // Save the current node context so we can call complete-node after battle
      combatNodeRef.current = { type: 'map', nodeId: run.currentNodeId };
      await apiFetch('/start-combat', 'POST');
      // Battle initiated via socket — CombatInterface will appear
      // DelveModal stays mounted; combat is detected via prevBattleRef
    } catch (err) {
      combatNodeRef.current = null;
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBoss = async () => {
    try {
      setActionLoading(true);
      setError(null);
      combatNodeRef.current = { type: 'boss', nodeId: run.currentNodeId };
      await apiFetch('/start-boss', 'POST');
    } catch (err) {
      combatNodeRef.current = null;
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAbandon = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await apiFetch('/abandon', 'POST');
      setRunSummary({ victory: false, ...data });
      setRun(null);
      setNodeContent(null);
      setScreen('run_abandoned');
      if (onCharacterUpdate) onCharacterUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
      setConfirmAbandon(false);
    }
  };

  // ── Render helpers ─────────────────────────────

  const hpPct = character
    ? Math.round(((character.max_hp - character.damage) / character.max_hp) * 100)
    : 100;
  const hpCurrent = character ? character.max_hp - character.damage : 0;
  const hpMax     = character ? character.max_hp : 0;

  // ── Render ─────────────────────────────────────
  return (
    <div className="delve-overlay">
      <div className="delve-modal">

        {/* ── Header ──────────────────────────── */}
        <div className="delve-header">
          <span className="delve-header-icon">⚔️</span>
          <span className="delve-header-title">The Delve</span>
          <button className="delve-close-btn" onClick={onClose} title="Close">✕</button>
        </div>

        {/* ── Body ────────────────────────────── */}
        <div className="delve-body">

          {loading && (
            <div className="delve-center">
              <div className="delve-spinner" />
              <p>Loading Delve…</p>
            </div>
          )}

          {!loading && error && (
            <div className="delve-error-box">
              <p>⚠ {error}</p>
              <button className="delve-btn delve-btn-secondary" onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          {/* ── ENTRY SCREEN ─────────────────── */}
          {!loading && screen === 'entry' && (
            <ScreenEntry
              entry={entry}
              tokenCost={tokenCost}
              character={character}
              actionLoading={actionLoading}
              onStart={handleStart}
              onResume={fetchState}
              onBuyToken={handleBuyToken}
              onClose={onClose}
              streak={streak}
              streakTitle={streakTitle}
              onOpenGuide={onOpenGuide}
            />
          )}

          {/* ── MAP SCREEN ───────────────────── */}
          {!loading && screen === 'map' && run && (
            <ScreenMap
              run={run}
              modifier={modifier}
              character={character}
              hpCurrent={hpCurrent}
              hpMax={hpMax}
              hpPct={hpPct}
              actionLoading={actionLoading}
              confirmAbandon={confirmAbandon}
              setConfirmAbandon={setConfirmAbandon}
              onEnterNode={handleEnterNode}
              onAbandon={handleAbandon}
            />
          )}

          {/* ── EVENT SCREEN ─────────────────── */}
          {!loading && screen === 'node_event' && nodeContent && (
            <ScreenEvent
              event={nodeContent.event}
              character={character}
              actionLoading={actionLoading}
              onChoose={handleResolveEvent}
            />
          )}

          {/* ── EVENT RESULT ─────────────────── */}
          {!loading && (screen === 'event_result') && result && (
            <ScreenResult
              title="Event Resolved"
              result={result}
              onContinue={() => { setResult(null); setScreen('map'); }}
            />
          )}

          {/* ── CHEST SCREEN ─────────────────── */}
          {!loading && screen === 'node_chest' && nodeContent && (
            <ScreenChest
              nodeContent={nodeContent}
              character={character}
              actionLoading={actionLoading}
              onOpen={handleOpenChest}
              onClaimLoot={handleClaimChestLoot}
              onPickCard={handlePickCard}
              onSkipCard={handleSkipCard}
              onClose={handleCloseChest}
            />
          )}

          {/* ── CHEST RESULT (legacy path, kept for safety) ──── */}
          {!loading && screen === 'chest_result' && result && (
            <ScreenChestResult
              rewards={result}
              onContinue={() => { setResult(null); setScreen('map'); }}
            />
          )}

          {/* ── CARD PICK SCREEN (event source) ─ */}
          {!loading && screen === 'node_card_pick' && nodeContent?.cardChoices && (
            <ScreenCardPick
              cards={nodeContent.cardChoices}
              actionLoading={actionLoading}
              onPick={handlePickCard}
              onSkip={handleSkipCard}
            />
          )}

          {/* ── CARD PICKED RESULT ───────────── */}
          {!loading && screen === 'card_picked' && result && (
            <div className="delve-section">
              <h3 className="delve-section-title">✨ Card Added</h3>
              {result.cardPicked && (
                <div className="delve-card-preview">
                  <div className="delve-card-name">{result.cardPicked.name}</div>
                  {result.cardPicked.effect && (
                    <div className="delve-card-effect">{result.cardPicked.effect}</div>
                  )}
                </div>
              )}
              <p className="delve-text">{result.message}</p>
              <button className="delve-btn delve-btn-primary" onClick={() => { setResult(null); setScreen('map'); }}>
                Return to Map
              </button>
            </div>
          )}

          {/* ── SHRINE SCREEN ────────────────── */}
          {!loading && screen === 'node_shrine' && nodeContent && (
            <ScreenShrine
              offers={nodeContent.offers || []}
              actionLoading={actionLoading}
              onResolve={handleResolveShrine}
            />
          )}

          {/* ── SHRINE RESULT ─────────────────── */}
          {!loading && screen === 'shrine_result' && result && (
            <div className="delve-section">
              <div className="delve-event-art">⛩️</div>
              <h3 className="delve-section-title">Shrine</h3>
              <p className="delve-text">{result.message}</p>
              {result.newModifier && (
                <div className="delve-modifier-strip" style={{ marginTop: '0.5rem' }}>
                  {result.newModifier.icon} New modifier: <strong>{result.newModifier.name}</strong> — {result.newModifier.description}
                </div>
              )}
              <button className="delve-btn delve-btn-primary" onClick={() => { setResult(null); setScreen('map'); }}>
                Return to Map
              </button>
            </div>
          )}

          {/* ── REST SCREEN ──────────────────── */}
          {!loading && screen === 'node_rest' && (
            <ScreenRest
              hpCurrent={hpCurrent}
              hpMax={hpMax}
              hpPct={hpPct}
              character={character}
              actionLoading={actionLoading}
              onRest={handleRest}
              nodeContent={run?.activeNodeContent}
            />
          )}

          {/* ── REST RESULT ──────────────────── */}
          {!loading && screen === 'rest_result' && result && (
            <div className="delve-section">
              <h3 className="delve-section-title">🔥 Rest Site</h3>
              <p className="delve-text">{result.message}</p>
              <button className="delve-btn delve-btn-primary" onClick={() => { setResult(null); setScreen('map'); }}>
                Return to Map
              </button>
            </div>
          )}

          {/* ── COMBAT SCREEN ────────────────── */}
          {!loading && screen === 'node_combat' && nodeContent && (
            <ScreenCombat
              content={nodeContent}
              currentBattle={currentBattle}
              actionLoading={actionLoading}
              onStartCombat={handleStartCombat}
            />
          )}

          {/* ── COMBAT RESULT ────────────────── */}
          {!loading && screen === 'combat_result' && result && (
            <div className="delve-section">
              <h3 className="delve-section-title">⚔️ Victory!</h3>
              <p className="delve-text">{result.message}</p>
              {result.milestone && (
                <div className="delve-milestone-badge">
                  🏆 Milestone! +{result.milestone.goldAwarded} gold and +{result.milestone.xpAwarded || 0} XP
                </div>
              )}
              <button className="delve-btn delve-btn-primary" onClick={() => { setResult(null); setScreen('map'); }}>
                Continue
              </button>
            </div>
          )}

          {/* ── BOSS SCREEN ──────────────────── */}
          {!loading && screen === 'node_boss' && nodeContent && (
            <ScreenBoss
              content={nodeContent}
              currentBattle={currentBattle}
              actionLoading={actionLoading}
              onStartBoss={handleStartBoss}
            />
          )}

          {/* ── RUN COMPLETE ─────────────────── */}
          {!loading && screen === 'run_complete' && runSummary && (
            <ScreenRunComplete summary={runSummary} onClose={onClose} />
          )}

          {/* ── RUN ABANDONED ────────────────── */}
          {!loading && screen === 'run_abandoned' && runSummary && (
            <ScreenRunAbandoned summary={runSummary} onClose={onClose} />
          )}

        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

// ── Entry Screen ───────────────────────────────────────────────
const ScreenEntry = ({ entry, tokenCost, character, actionLoading, onStart, onResume, onBuyToken, onClose, streak, streakTitle, onOpenGuide }) => {
  const gold = character?.gold || 0;
  const canAfford = tokenCost !== null && gold >= tokenCost;
  return (
  <div className="delve-section">
    <div className="delve-entrance-art">⚔️</div>
    <h2 className="delve-title">The Delve Entrance</h2>
    <p className="delve-lore">
      Before you stands a crumbling archway. Ancient runes pulse with dark energy.
      Those brave enough to enter face 12 floors of monsters, traps, and treasures —
      culminating in a powerful boss who guards legendary rewards.
    </p>

    {onOpenGuide && (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
        <button
          type="button"
          onClick={() => onOpenGuide('challenge-loops')}
          style={{
            background: 'rgba(212, 175, 55, 0.12)',
            border: '1px solid rgba(212, 175, 55, 0.32)',
            color: '#f0d58c',
            borderRadius: '999px',
            padding: '0.45rem 0.9rem',
            cursor: 'pointer'
          }}
        >
          📘 Delve Rewards Guide
        </button>
      </div>
    )}

    {/* v400: Streak indicator */}
    {streak > 0 && (
      <div className="delve-streak-banner" style={{
        background: 'linear-gradient(135deg, #2c1654 0%, #4a1a7a 100%)',
        border: '1px solid #8e44ad',
        borderRadius: '8px',
        padding: '0.6rem 1rem',
        marginBottom: '0.75rem',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#e8d44d' }}>
          🔥 Win Streak: {streak} {streakTitle && `— ${streakTitle}`}
        </div>
        <div style={{ fontSize: '0.82rem', color: '#cba6f7', marginTop: '2px' }}>
          Monsters are {streak * 10}% harder. Greater glory awaits.
        </div>
      </div>
    )}
    <div className="delve-info-box">
      <div className="delve-info-row">🗺 <strong>12 Floors</strong> of branching node paths</div>
      <div className="delve-info-row">⚔️ Combat, Events, Chests, Shrines &amp; Rest sites</div>
      <div className="delve-info-row">🃏 Win cards for your deck — permanently on boss kill</div>
      <div className="delve-info-row">💰 Milestone gold kept even if you abandon</div>
    </div>

    {entry && (
      <div className="delve-entry-status">
        {entry.isFree && (
          <div className="delve-status-badge delve-status-free">✅ Free daily entry available</div>
        )}
        {!entry.isFree && entry.canEnter && !entry.hasActiveRun && (
          <div className="delve-status-badge delve-status-token">🎟 Delve Token will be consumed</div>
        )}
        {entry.hasActiveRun && (
          <div className="delve-status-badge delve-status-resume">▶ Active run in progress — resume below</div>
        )}
        {!entry.canEnter && !entry.hasActiveRun && entry.locked && (
          <div className="delve-locked-gate">
            <div className="delve-locked-title">🔒 The Delve is sealed</div>
            {entry.lockReason === 'level' && (
              <div className="delve-locked-requirement">
                <span>⚔️ Adventure more — reach <strong>level {entry.lockProgress.required}</strong></span>
                <div className="delve-gate-bar-wrap">
                  <div
                    className="delve-gate-bar-fill"
                    style={{ width: `${Math.min(100, (entry.lockProgress.current / entry.lockProgress.required) * 100)}%` }}
                  />
                  <span className="delve-gate-bar-label">
                    Level {entry.lockProgress.current} / {entry.lockProgress.required}
                  </span>
                </div>
              </div>
            )}
            {entry.lockReason === 'exploration' && (
              <div className="delve-locked-requirement">
                <span>🗺️ Explore the world — visit <strong>{entry.lockProgress.required} areas</strong></span>
                <div className="delve-gate-bar-wrap">
                  <div
                    className="delve-gate-bar-fill"
                    style={{ width: `${Math.min(100, (entry.lockProgress.current / entry.lockProgress.required) * 100)}%` }}
                  />
                  <span className="delve-gate-bar-label">
                    {entry.lockProgress.current} / {entry.lockProgress.required} areas explored
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        {!entry.canEnter && !entry.hasActiveRun && !entry.locked && (
          <div className="delve-status-badge delve-status-locked">🔒 {entry.reason}</div>
        )}
      </div>
    )}

    {/* Buy Token panel — shown when eligible to run but free entry used and no token held */}
    {entry && !entry.canEnter && !entry.hasActiveRun && !entry.locked && tokenCost !== null && (
      <div className="delve-buy-token-panel">
        <div className="delve-buy-token-title">🎟 Purchase a Delve Token</div>
        <div className="delve-buy-token-cost">
          {formatCurrency(tokenCost)}<span className="delve-buy-token-g">g</span>
        </div>
        <div className="delve-buy-token-sub">Price scales with your level</div>
        {!canAfford && (
          <div className="delve-buy-token-broke">You have {formatCurrency(gold)}g — not enough gold</div>
        )}
        <button
          className={`delve-btn ${canAfford ? 'delve-btn-primary' : 'delve-btn-disabled'}`}
          onClick={onBuyToken}
          disabled={actionLoading || !canAfford}
        >
          {actionLoading ? '…' : canAfford ? `Buy Token (${formatCurrency(tokenCost)}g)` : 'Insufficient Gold'}
        </button>
      </div>
    )}

    <div className="delve-btn-row">
      {entry?.hasActiveRun && (
        <button
          className="delve-btn delve-btn-primary"
          onClick={onResume}
          disabled={actionLoading || !entry?.canEnter}
        >
          {actionLoading ? '…' : '▶ Resume Run'}
        </button>
      )}
      {!entry?.hasActiveRun && (
        <button
          className="delve-btn delve-btn-primary"
          onClick={onStart}
          disabled={actionLoading || !entry?.canEnter}
        >
          {actionLoading ? '…' : '⚔️ Enter the Delve'}
        </button>
      )}
      <button className="delve-btn delve-btn-secondary" onClick={onClose}>
        Not Yet
      </button>
    </div>
  </div>
  );
};

// ── Map Screen ─────────────────────────────────────────────────
const ScreenMap = ({ run, modifier, character, hpCurrent, hpMax, hpPct, actionLoading,
                     confirmAbandon, setConfirmAbandon, onEnterNode, onAbandon }) => {
  const { map, currentFloor, modifierId, pendingCards, milestoneGold } = run;

  // Figure out reachable nodes (client-side mirror of server getReachableNodes)
  const getReachable = () => {
    const { currentNodeId, currentFloor, lastCompletedNodeId, lastNodeType } = run;
    // Helper: find a node object by id across the whole map
    const findNode = (id) => { for (const fl of map) for (const n of fl) if (n.id === id) return n; return null; };

    // No consecutive rests (mirrors server getReachableNodes, no hard cap)
    // Exception: if the last rest was forced (no alternative path), don't penalise the next floor.
    // Safety valve: if filtering would remove ALL options, skip the filter to prevent deadlock.
    const filterRest = (ids) => {
      if (!(lastNodeType === 'rest' && !run.lastRestWasForced)) return ids;
      const filtered = ids.filter(id => { const n = findNode(id); return !n || n.type !== 'rest'; });
      return filtered.length > 0 ? filtered : ids; // never return empty
    };

    if (!currentNodeId) {
      if (!currentFloor || currentFloor <= 1) return map[0].map(n => n.id);
      // Between floors: use lastCompletedNodeId (the chosen path)
      if (lastCompletedNodeId) {
        const last = findNode(lastCompletedNodeId);
        if (last) return filterRest(last.connections);
      }
      return [];
    }
    // Sitting on a completed node — its connections are next
    for (const floor of map) {
      for (const node of floor) {
        if (node.id === currentNodeId && node.completed) return filterRest(node.connections);
      }
    }
    return [];
  };
  const reachable = new Set(getReachable());

  const hpColor = hpPct > 60 ? '#27ae60' : hpPct > 30 ? '#f39c12' : '#c0392b';

  return (
    <div className="delve-map-screen">
      {/* Status bar */}
      <div className="delve-status-bar">
        <div className="delve-hp-bar-wrap" title={`${hpCurrent} / ${hpMax} HP`}>
          <div className="delve-hp-label">❤️ {hpCurrent}/{hpMax}</div>
          <div className="delve-hp-track">
            <div className="delve-hp-fill" style={{ width: `${hpPct}%`, background: hpColor }} />
          </div>
        </div>
        <div className="delve-status-right">
          {pendingCards && pendingCards.length > 0 && (
            <span className="delve-badge delve-badge-cards">🃏 {pendingCards.length} card{pendingCards.length > 1 ? 's' : ''}</span>
          )}
          {milestoneGold > 0 && (
            <span className="delve-badge delve-badge-gold">💰 {milestoneGold}g kept</span>
          )}
        </div>
      </div>

      {modifier && (
        <div className="delve-modifier-strip">
          ⚡ Run modifier: <strong>{modifier.name}</strong> — {modifier.description}
        </div>
      )}

      {/* ── STS-style SVG branching-path map ── */}
      {(() => {
        const SVG_W  = 340;
        const LABEL_W = 28;      // left margin for floor labels
        const FLOOR_H = 58;      // vertical pixels per floor
        const NODE_R  = 18;      // node circle radius
        const SVG_H   = map.length * FLOOR_H + 28;

        // Pre-compute pixel coordinates for every node
        const coords = {};
        map.forEach((floorNodes, fi) => {
          const y     = fi * FLOOR_H + NODE_R + 14;
          const count = floorNodes.length;
          floorNodes.forEach(node => {
            let x;
            if (count === 1) {
              x = LABEL_W + (SVG_W - LABEL_W) / 2;
            } else {
              const margin = 36;
              const usable = (SVG_W - LABEL_W) - 2 * margin;
              x = LABEL_W + margin + (node.pos / (count - 1)) * usable;
            }
            coords[node.id] = { x, y };
          });
        });

        return (
          <div className="delve-svg-map-wrap">
            <svg width={SVG_W} height={SVG_H} className="delve-svg-map">

              {/* Floor labels */}
              {map.map((_, fi) => {
                const floorNum   = fi + 1;
                const y          = fi * FLOOR_H + NODE_R + 14;
                const isCurFloor = floorNum === currentFloor;
                return (
                  <text key={`fl${floorNum}`}
                    x={LABEL_W / 2} y={y}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize="9" fontWeight={isCurFloor ? '700' : '400'}
                    fill={isCurFloor ? '#a78bfa' : '#6b7280'}
                  >
                    {floorNum === 12 ? '👑' : `F${floorNum}`}
                  </text>
                );
              })}

              {/* Connection lines — 3 visual states */}
              {(() => {
                const completedSet = new Set();
                map.forEach(fl => fl.forEach(n => { if (n.completed) completedSet.add(n.id); }));
                const nodeTypeMap = {};
                map.forEach(fl => fl.forEach(n => { nodeTypeMap[n.id] = n.type; }));
                return map.flatMap(floorNodes =>
                  floorNodes.flatMap(node =>
                    node.connections.map(connId => {
                      const from = coords[node.id];
                      const to   = coords[connId];
                      if (!from || !to) return null;
                      const fromDone  = completedSet.has(node.id);
                      const toDone    = completedSet.has(connId);
                      const toNext    = reachable.has(connId);
                      const midY = (from.y + to.y) / 2;
                      let stroke, sw, dash, opacity;
                      if (fromDone && toDone) {
                        // Traversed path — solid muted
                        stroke = '#4b5563'; sw = 2; dash = ''; opacity = 0.55;
                      } else if (fromDone && toNext) {
                        // Available next choice — bright dashed in node's color
                        stroke = NODE_COLOR[nodeTypeMap[connId]] || '#7c3aed';
                        sw = 2; dash = '6,3'; opacity = 0.85;
                      } else {
                        // Future / locked path — visible dotted so player can see the map structure
                        stroke = '#3d4a5c'; sw = 1.5; dash = '4,4'; opacity = 0.6;
                      }
                      return (
                        <path
                          key={`${node.id}>${connId}`}
                          d={`M${from.x},${from.y} C${from.x},${midY} ${to.x},${midY} ${to.x},${to.y}`}
                          fill="none" stroke={stroke} strokeWidth={sw}
                          strokeDasharray={dash} opacity={opacity}
                        />
                      );
                    })
                  )
                );
              })()}

              {/* Nodes */}
              {map.flatMap(floorNodes =>
                floorNodes.map(node => {
                  const isReachable = reachable.has(node.id);
                  const isCompleted = node.completed;
                  const isCurrent   = node.id === run.currentNodeId;
                  const c           = coords[node.id];
                  if (!c) return null;

                  let fill, stroke, sw, textOp;
                  if (isCompleted)      { fill = '#1a2332'; stroke = '#374151'; sw = 2;   textOp = 0.45; }
                  else if (isCurrent)   { fill = '#1e1050'; stroke = '#a78bfa'; sw = 3;   textOp = 1;    }
                  else if (isReachable) { fill = '#0f1623'; stroke = NODE_COLOR[node.type]; sw = 2.5; textOp = 1; }
                  else                  { fill = '#0f1623'; stroke = '#2d3748'; sw = 1.5; textOp = 0.22; }

                  const clickable = isReachable && !isCompleted && !actionLoading;
                  return (
                    <g key={node.id}
                      transform={`translate(${c.x},${c.y})`}
                      style={{ cursor: clickable ? 'pointer' : 'default' }}
                      onClick={() => clickable && onEnterNode(node.id)}
                    >
                      {/* Ambient glow for reachable nodes */}
                      {isReachable && !isCompleted && (
                        <circle r={NODE_R + 6} fill={NODE_COLOR[node.type]} opacity={0.13} />
                      )}
                      {/* Current-node outer ring */}
                      {isCurrent && (
                        <circle r={NODE_R + 5} fill="none" stroke="#a78bfa" strokeWidth={1.5} opacity={0.5} strokeDasharray="4,3" />
                      )}
                      {/* Main circle */}
                      <circle r={NODE_R} fill={fill} stroke={stroke} strokeWidth={sw} />
                      {/* Icon */}
                      <text textAnchor="middle" dominantBaseline="central"
                        fontSize={node.type === 'boss' ? 14 : 13}
                        opacity={textOp}
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >
                        {isCompleted ? '✓' : NODE_ICON[node.type]}
                      </text>
                      {/* Type label beneath reachable nodes */}
                      {isReachable && !isCompleted && (
                        <text y={NODE_R + 11} textAnchor="middle"
                          fontSize="8" fill={NODE_COLOR[node.type]} opacity={0.9}
                          style={{ userSelect: 'none', pointerEvents: 'none' }}
                        >
                          {NODE_LABEL[node.type]}
                        </text>
                      )}
                    </g>
                  );
                })
              )}
            </svg>
          </div>
        );
      })()}

      {/* Abandon */}
      <div className="delve-map-footer">
        {!confirmAbandon ? (
          <button className="delve-btn delve-btn-danger" onClick={() => setConfirmAbandon(true)}>
            Abandon Run
          </button>
        ) : (
          <div className="delve-confirm-row">
            <span>Abandon and keep {milestoneGold || 0}g?</span>
            <button className="delve-btn delve-btn-danger" onClick={onAbandon} disabled={actionLoading}>
              {actionLoading ? '…' : 'Yes, Abandon'}
            </button>
            <button className="delve-btn delve-btn-secondary" onClick={() => setConfirmAbandon(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Event Screen ───────────────────────────────────────────────
const ScreenEvent = ({ event, character, actionLoading, onChoose }) => {
  if (!event) return null;

  const isOptionAvailable = (opt) => {
    if (opt.requiresClass) {
      // requiresClass is stored as an array on the server (e.g. ['Mage', 'Psion'])
      const classes = Array.isArray(opt.requiresClass) ? opt.requiresClass : [opt.requiresClass];
      const charClasses = [character?.class, character?.advanced_class].filter(Boolean).map(c => c.toLowerCase());
      if (!classes.some(c => charClasses.includes(c.toLowerCase()))) return false;
    }
    if (opt.requiresAlignment && (!character?.alignment || !character.alignment.toLowerCase().includes(opt.requiresAlignment.toLowerCase()))) return false;
    if (opt.requiresItem) {
      const inv = character?.inventory || [];
      if (!inv.some(i => i && i.name === opt.requiresItem)) return false;
    }
    if (opt.goldCost && (character?.gold || 0) < opt.goldCost) return false;
    return true;
  };

  return (
    <div className="delve-section">
      <div className="delve-event-art">📜</div>
      <h3 className="delve-section-title">{event.name}</h3>
      <p className="delve-event-desc">{event.description}</p>
      <div className="delve-event-options">
        {event.options.map(opt => {
          const available = isOptionAvailable(opt);
          return (
            <button
              key={opt.id}
              className={`delve-event-option ${available ? '' : 'delve-option-locked'}`}
              disabled={!available || actionLoading}
              onClick={() => available && onChoose(event.id, opt.id)}
              title={!available ? 'Requirements not met' : opt.text}
            >
              <span className="delve-option-text">{opt.text}</span>
              {opt.requiresClass     && <span className="delve-option-req req-class">  [{Array.isArray(opt.requiresClass) ? opt.requiresClass.join('/') : opt.requiresClass}]</span>}
              {opt.requiresAlignment && <span className="delve-option-req req-align">  [{opt.requiresAlignment}]</span>}
              {opt.requiresItem      && <span className="delve-option-req req-item">   [needs {opt.requiresItem}]</span>}
              {opt.goldCost && (character?.gold || 0) < opt.goldCost && <span className="delve-option-req req-gold">  [need {opt.goldCost} gold]</span>}
              {!available            && <span className="delve-option-lock"> 🔒</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Event Result Screen ────────────────────────────────────────
const ScreenResult = ({ title, result, onContinue }) => (
  <div className="delve-section">
    <div className="delve-event-art">⚡</div>
    <h3 className="delve-section-title">{title}</h3>
    {result.message && <p className="delve-text">{result.message}</p>}
    <div className="delve-rewards-list">
      {result.goldChange   > 0 && <div className="delve-reward-item">💰 +{result.goldChange} gold</div>}
      {result.goldChange   < 0 && <div className="delve-reward-item loss">💰 {result.goldChange} gold</div>}
      {result.xpChange     > 0 && <div className="delve-reward-item">✨ +{result.xpChange} XP</div>}
      {result.hpChange     > 0 && <div className="delve-reward-item">❤️ +{result.hpChange} HP restored</div>}
      {result.hpChange     < 0 && <div className="delve-reward-item loss">❤️ {result.hpChange} HP lost</div>}
      {result.mpChange     > 0 && <div className="delve-reward-item">💧 +{result.mpChange} MP restored</div>}
      {result.forgeTokens  > 0 && <div className="delve-reward-item">🔧 +{result.forgeTokens} Forge Token{result.forgeTokens > 1 ? 's' : ''}</div>}
      {result.items && result.items.map((item, i) => (
        <div key={i} className="delve-reward-item">📦 {item.name}</div>
      ))}
      {result.cursed && <div className="delve-reward-item loss">💀 You have been cursed!</div>}
    </div>
    <button className="delve-btn delve-btn-primary" onClick={onContinue}>Continue</button>
  </div>
);

// ── Chest Screen ───────────────────────────────────────────────
const TIER_LABEL = { common: 'Common', uncommon: 'Uncommon', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' };
const TIER_ICON  = { common: '📦', uncommon: '💙', rare: '💜', epic: '⚡', legendary: '🌟' };

/**
 * Combined chest screen: shows the sealed chest, then after opening shows all
 * rewards in a single panel. Player claims each reward independently and can
 * move on at any time, skipping unclaimed rewards.
 */
const ScreenChest = ({ nodeContent, character, actionLoading, onOpen, onClaimLoot, onPickCard, onSkipCard, onClose }) => {
  // Local state for the card-detail overlay
  const [inspecting, setInspecting] = React.useState(null); // card being inspected

  const tier     = nodeContent.tier;
  const opened   = nodeContent.opened;
  const rewards  = nodeContent.rewards || null;
  const lootClaimed = nodeContent.lootClaimed;
  const cardClaimed = nodeContent.cardClaimed;
  const cardChoices = rewards?.cardChoices || [];

  if (!opened) {
    return (
      <div className="delve-section">
        <div className="delve-event-art">{TIER_ICON[tier] || '📦'}</div>
        <h3 className="delve-section-title">{TIER_LABEL[tier] || tier} Chest</h3>
        <p className="delve-text">You find a sealed chest. Claim your reward.</p>
        <button className="delve-btn delve-btn-primary" disabled={actionLoading} onClick={onOpen}>
          {actionLoading ? '…' : 'Open Chest'}
        </button>
      </div>
    );
  }

  // Chest is open — show all rewards
  const allItems = [...(rewards.items || []), ...(rewards.potions || [])];

  // Cursed chest: show the curse outcome prominently instead of normal loot UI
  if (rewards.cursed) {
    return (
      <div className="delve-section">
        <div className="delve-event-art">💀</div>
        <h3 className="delve-section-title">Cursed Chest!</h3>
        <div className="delve-modifier-strip" style={{ background: '#3a1a1a', borderColor: '#7f1d1d' }}>
          ☠️ {rewards.cursedOutcome?.message || 'The chest was cursed! You suffer the consequences.'}
        </div>
        <p className="delve-text" style={{ color: '#9ca3af', marginTop: '0.75rem' }}>There are no rewards here — only misfortune.</p>
        <button
          className="delve-btn delve-btn-secondary"
          disabled={actionLoading}
          onClick={onClose}
          style={{ marginTop: '0.75rem' }}
        >
          Move On →
        </button>
      </div>
    );
  }

  return (
    <div className="delve-section">
      {/* Card inspect overlay */}
      {inspecting && (
        <div className="delve-card-inspect-overlay">
          <div className="delve-card-inspect-box">
            <div className={`delve-card-inspect-rarity rarity-${inspecting.rarity || 'common'}`}>
              {inspecting.rarity || 'common'}
            </div>
            <div className="delve-card-inspect-name">{inspecting.name}</div>
            {inspecting.effect && <p className="delve-card-inspect-effect">{inspecting.effect}</p>}
            {inspecting.description && <p className="delve-card-inspect-desc">{inspecting.description}</p>}
            {inspecting.damage  && <div className="delve-card-inspect-stat">⚔ Damage: {inspecting.damage}</div>}
            {inspecting.ap      && <div className="delve-card-inspect-stat">⚡ AP Cost: {inspecting.ap}</div>}
            {inspecting.classes && <div className="delve-card-inspect-stat">🎭 {Array.isArray(inspecting.classes) ? inspecting.classes.join(', ') : inspecting.classes}</div>}
            <div className="delve-card-inspect-note">
              🃏 Added to your run — becomes permanent if you defeat the boss.
            </div>
            <div className="delve-btn-row">
              <button
                className="delve-btn delve-btn-primary"
                disabled={actionLoading}
                onClick={() => { setInspecting(null); onPickCard(inspecting.name); }}
              >
                {actionLoading ? '…' : 'Add to Run'}
              </button>
              <button className="delve-btn delve-btn-secondary" onClick={() => setInspecting(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="delve-event-art">{TIER_ICON[tier] || '📦'}</div>
      <h3 className="delve-section-title">{TIER_LABEL[tier] || tier} Chest — Opened!</h3>

      <div className="delve-chest-rewards">

        {/* Gold + items loot block */}
        <div className={`delve-chest-reward-block ${lootClaimed ? 'claimed' : ''}`}>
          <div className="delve-chest-reward-header">
            <span>💰 Loot</span>
            {lootClaimed && <span className="delve-claimed-badge">✓ Claimed</span>}
          </div>
          <div className="delve-chest-loot-list">
            {rewards.gold > 0 && (
              <div className="delve-chest-loot-item">💰 {rewards.gold} gold</div>
            )}
            {rewards.xp > 0 && (
              <div className="delve-chest-loot-item">✨ {rewards.xp} XP</div>
            )}
            {rewards.forgeTokens > 0 && (
              <div className="delve-chest-loot-item">🔧 {rewards.forgeTokens} Forge Token{rewards.forgeTokens > 1 ? 's' : ''}</div>
            )}
            {rewards.extraDeckSlot > 0 && (
              <div className="delve-chest-loot-item">✨ Extra Deck Slot</div>
            )}
            {allItems.map((item, i) => (
              <div key={i} className="delve-chest-loot-item">
                {(item.type || '').toLowerCase().includes('restore') ? '🧪' : '📦'} {item.name}
                {item.quantity > 1 && <span className="delve-chest-loot-qty"> ×{item.quantity}</span>}
              </div>
            ))}
          </div>
          {!lootClaimed && (
            <button
              className="delve-btn delve-btn-primary delve-chest-claim-btn"
              disabled={actionLoading}
              onClick={onClaimLoot}
            >
              {actionLoading ? '…' : 'Claim Loot'}
            </button>
          )}
        </div>

        {/* Card choice block (rare+ only) */}
        {cardChoices.length > 0 && (
          <div className={`delve-chest-reward-block ${cardClaimed ? 'claimed' : ''}`}>
            <div className="delve-chest-reward-header">
              <span>🃏 Choose a Card</span>
              {cardClaimed && <span className="delve-claimed-badge">✓ Claimed</span>}
            </div>
            {!cardClaimed && (
              <>
                <p className="delve-text small">Click a card to preview it before adding to your run.</p>
                <div className="delve-card-choices">
                  {cardChoices.map((card, i) => (
                    <button
                      key={i}
                      className="delve-card-option"
                      disabled={actionLoading}
                      onClick={() => setInspecting(card)}
                    >
                      <div className="delve-card-opt-name">{card.name}</div>
                      {card.description && <div className="delve-card-opt-effect">{card.description}</div>}
                      {card.damage  && <div className="delve-card-opt-stat">⚔ {card.damage}</div>}
                      {card.rarity  && <div className={`delve-card-rarity rarity-${card.rarity}`}>{card.rarity}</div>}
                    </button>
                  ))}
                </div>
                <button
                  className="delve-btn delve-btn-secondary"
                  disabled={actionLoading}
                  onClick={onSkipCard}
                  style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}
                >
                  Skip — I don't want any of these
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <button
        className="delve-btn delve-btn-secondary"
        disabled={actionLoading}
        onClick={onClose}
        style={{ marginTop: '0.75rem' }}
      >
        Move On →
      </button>
    </div>
  );
};

const ScreenChestResult = ({ rewards, onContinue }) => (
  <div className="delve-section">
    <div className="delve-event-art">{TIER_ICON[rewards.tier] || '📦'}</div>
    <h3 className="delve-section-title">Chest Opened!</h3>
    <div className="delve-rewards-list">
      {rewards.gold > 0 && <div className="delve-reward-item">💰 +{rewards.gold} gold</div>}
      {rewards.xp > 0 && <div className="delve-reward-item">✨ +{rewards.xp} XP</div>}
      {rewards.forgeTokens > 0 && <div className="delve-reward-item">🔧 +{rewards.forgeTokens} Forge Token{rewards.forgeTokens > 1 ? 's' : ''}</div>}
      {rewards.extraDeckSlot && <div className="delve-reward-item">✨ Extra Deck Slot unlocked!</div>}
      {rewards.items && rewards.items.map((item, i) => (
        <div key={i} className="delve-reward-item">📦 {item.name}</div>
      ))}
    </div>
    <button className="delve-btn delve-btn-primary" onClick={onContinue}>Continue</button>
  </div>
);

// ── Card Pick Screen (events / non-chest sources) ───────────────
const ScreenCardPick = ({ cards, actionLoading, onPick, onSkip }) => {
  const [inspecting, setInspecting] = React.useState(null);

  return (
    <div className="delve-section">
      {inspecting && (
        <div className="delve-card-inspect-overlay">
          <div className="delve-card-inspect-box">
            <div className={`delve-card-inspect-rarity rarity-${inspecting.rarity || 'common'}`}>
              {inspecting.rarity || 'common'}
            </div>
            <div className="delve-card-inspect-name">{inspecting.name}</div>
            {inspecting.effect && <p className="delve-card-inspect-effect">{inspecting.effect}</p>}
            {inspecting.description && <p className="delve-card-inspect-desc">{inspecting.description}</p>}
            {inspecting.damage && <div className="delve-card-inspect-stat">⚔ Damage: {inspecting.damage}</div>}
            {inspecting.ap     && <div className="delve-card-inspect-stat">⚡ AP Cost: {inspecting.ap}</div>}
            {inspecting.classes && <div className="delve-card-inspect-stat">🎭 {Array.isArray(inspecting.classes) ? inspecting.classes.join(', ') : inspecting.classes}</div>}
            <div className="delve-card-inspect-note">
              🃏 Added to your run — becomes permanent if you defeat the boss.
            </div>
            <div className="delve-btn-row">
              <button
                className="delve-btn delve-btn-primary"
                disabled={actionLoading}
                onClick={() => { setInspecting(null); onPick(inspecting.name); }}
              >
                {actionLoading ? '…' : 'Add to Run'}
              </button>
              <button className="delve-btn delve-btn-secondary" onClick={() => setInspecting(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="delve-event-art">🃏</div>
      <h3 className="delve-section-title">Choose a Card</h3>
      <p className="delve-text">Click a card to preview it before adding to your run.</p>
      <div className="delve-card-choices">
        {cards.map((card, i) => (
          <button
            key={i}
            className="delve-card-option"
            disabled={actionLoading}
            onClick={() => setInspecting(card)}
          >
            <div className="delve-card-opt-name">{card.name}</div>
            {card.description && <div className="delve-card-opt-effect">{card.description}</div>}
            {card.damage  && <div className="delve-card-opt-stat">⚔ {card.damage}</div>}
            {card.rarity  && <div className={`delve-card-rarity rarity-${card.rarity}`}>{card.rarity}</div>}
          </button>
        ))}
      </div>
      {onSkip && (
        <button
          className="delve-btn delve-btn-secondary"
          disabled={actionLoading}
          onClick={onSkip}
          style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}
        >
          Skip — I don't want any of these
        </button>
      )}
    </div>
  );
};

// ── Shrine Screen ──────────────────────────────────────────────
const ScreenShrine = ({ offers, actionLoading, onResolve }) => (
  <div className="delve-section">
    <div className="delve-event-art">⛩️</div>
    <h3 className="delve-section-title">Mysterious Shrine</h3>
    <p className="delve-text">
      Ancient power radiates from the shrine. It offers bargains — but every gift has a price.
    </p>
    <p className="delve-text" style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '-0.5rem' }}>
      Gold, tokens, and card changes are finalized only when you complete the run.
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', margin: '1rem 0' }}>
      {offers.map(offer => (
        <button
          key={offer.id}
          disabled={!offer.available || actionLoading}
          onClick={() => offer.available && onResolve(offer.id)}
          style={{
            display: 'block', width: '100%', padding: '0.75rem 1rem',
            background: offer.available ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.3)',
            border: `1px solid ${offer.available ? '#555' : '#333'}`,
            borderRadius: '8px',
            cursor: offer.available ? 'pointer' : 'not-allowed',
            opacity: offer.available ? 1 : 0.5,
            textAlign: 'left', color: '#e0e0e0',
            transition: 'border-color 0.2s, background 0.2s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '1.3rem' }}>{offer.icon}</span>
            <span style={{ fontWeight: 'bold', fontSize: '1rem', color: '#fff' }}>{offer.name}</span>
          </div>
          <p style={{ fontSize: '0.85rem', color: '#9ca3af', margin: '0 0 0.5rem 0', lineHeight: 1.4 }}>
            {offer.description}
          </p>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
            <span style={{ color: '#ef4444', fontSize: '0.88rem', fontWeight: 'bold' }}>
              📉 {offer.costLabel}
            </span>
            <span style={{ color: '#22c55e', fontSize: '0.88rem', fontWeight: 'bold' }}>
              📈 {offer.rewardLabel}
            </span>
          </div>
          {!offer.available && offer.unavailableReason && (
            <div style={{ fontSize: '0.8rem', color: '#f87171', marginTop: '0.4rem' }}>
              🔒 {offer.unavailableReason}
            </div>
          )}
        </button>
      ))}
    </div>
    <button
      className="delve-btn delve-btn-secondary"
      disabled={actionLoading}
      onClick={() => onResolve('decline')}
      style={{ marginTop: '0.25rem' }}
    >
      Walk Away — Leave the shrine untouched
    </button>
  </div>
);

// ── Rest Screen ────────────────────────────────────────────────
const ScreenRest = ({ hpCurrent, hpMax, hpPct, character, actionLoading, onRest, nodeContent }) => {
  const hasDirtyDeck = () => {
    const JUNK = new Set(['wound', 'junk', 'curse', 'cursed', 'trauma', 'dread',
                          'Wound', 'Curse', 'Cursed', 'Jinx', 'Junk', 'Exhaustion',
                          'Trauma', 'Dread', 'Despair', 'Necrosis']);
    const deck = character.deck_cards || [];
    return deck.some(c => c && (JUNK.has(c.type) || JUNK.has(c.name)));
  };

  return (
    <div className="delve-section">
      <div className="delve-event-art">🔥</div>
      <h3 className="delve-section-title">Rest Site</h3>
      <p className="delve-text">You find a safe campfire. Choose how to spend this moment of respite.</p>
      <div className="delve-rest-options">
        <button className="delve-rest-opt" disabled={actionLoading || nodeContent?.healDisabled} onClick={() => onRest('heal')}>
          <div className="delve-rest-icon">❤️</div>
          <div className="delve-rest-label">Heal</div>
          <div className="delve-rest-desc">{nodeContent?.healDisabled ? 'Iron Discipline — healing unavailable' : 'Recover ~40% of max HP'}</div>
          <div className="delve-rest-stat">{hpCurrent}/{hpMax} HP ({hpPct}%)</div>
        </button>
        <button className="delve-rest-opt" disabled={actionLoading} onClick={() => onRest('cleanse')}>
          <div className="delve-rest-icon">🧹</div>
          <div className="delve-rest-label">Cleanse</div>
          <div className="delve-rest-desc">Burn negative cards from your deck</div>
          {hasDirtyDeck() && <div className="delve-rest-stat warn">⚠ Negative cards found</div>}
        </button>
        <button className="delve-rest-opt" disabled={actionLoading} onClick={() => onRest('meditate')}>
          <div className="delve-rest-icon">🧘</div>
          <div className="delve-rest-label">Meditate</div>
          <div className="delve-rest-desc">Gain a random combat buff for this run</div>
        </button>
      </div>
    </div>
  );
};

// ── Combat Screen ──────────────────────────────────────────────
const ScreenCombat = ({ content, currentBattle, actionLoading, onStartCombat }) => {
  const { monster } = content;
  if (!monster) return null;

  const activeDelveBattle = currentBattle
    && currentBattle.status === 'active'
    && (currentBattle.monster?._isDelveEncounter || currentBattle.monster?._isDelveBoss);

  if (activeDelveBattle) {
    return (
      <div className="delve-section delve-center">
        <div className="delve-spinner" />
        <p>Battle in progress…</p>
        <p className="delve-hint">Complete the fight in the combat window.</p>
      </div>
    );
  }

  return (
    <div className="delve-section">
      <div className="delve-event-art">⚔️</div>
      <h3 className="delve-section-title">Combat Encounter</h3>
      <div className="delve-monster-card">
        <div className="delve-monster-name">{monster.name}</div>
        <div className="delve-monster-stats">
          <span>❤️ {monster.hp} HP</span>
          <span>⚔️ ATT {monster.att}</span>
          <span>🛡 DEF {monster.def}</span>
          <span>Lv {monster.level}</span>
        </div>
      </div>
      <button
        className="delve-btn delve-btn-danger"
        disabled={actionLoading}
        onClick={onStartCombat}
      >
        {actionLoading ? 'Initiating…' : '⚔️ Engage!'}
      </button>
    </div>
  );
};

// ── Boss Screen ────────────────────────────────────────────────
const ScreenBoss = ({ content, currentBattle, actionLoading, onStartBoss }) => {
  const { boss } = content;

  const activeDelveBattle = currentBattle
    && currentBattle.status === 'active'
    && (currentBattle.monster?._isDelveBoss || currentBattle.monster?._isDelveEncounter);

  if (activeDelveBattle) {
    return (
      <div className="delve-section delve-center">
        <div className="delve-spinner" />
        <p>Boss battle in progress…</p>
      </div>
    );
  }

  return (
    <div className="delve-section">
      <div className="delve-event-art">💀</div>
      <h3 className="delve-section-title">Boss Chamber</h3>
      {boss && (
        <>
          <div className="delve-boss-name">{boss.name}</div>
          {boss.lore && <p className="delve-lore">{boss.lore}</p>}
          <div className="delve-monster-stats">
            <span>❤️ ~{boss.hp} HP</span>
            <span>Lv {boss.level}</span>
          </div>
        </>
      )}
      <p className="delve-warn">Defeat the boss to permanently add your earned cards to your deck!</p>
      <button
        className="delve-btn delve-btn-danger"
        disabled={actionLoading}
        onClick={onStartBoss}
      >
        {actionLoading ? 'Initiating…' : '💀 Enter Boss Chamber'}
      </button>
    </div>
  );
};

// ── Run Complete Screen ────────────────────────────────────────
const ScreenRunComplete = ({ summary, onClose }) => (
  <div className="delve-section delve-victory">
    <div className="delve-event-art">🏆</div>
    <h2 className="delve-title">Delve Conquered!</h2>
    <p className="delve-text">{summary.message}</p>
    {summary.streak > 0 && (
      <div style={{ textAlign: 'center', margin: '0.5rem 0', color: '#e8d44d', fontWeight: 'bold' }}>
        🔥 Win Streak: {summary.streak}{summary.streakTitle ? ` — ${summary.streakTitle}` : ''}
      </div>
    )}
    <div className="delve-rewards-list">
      {summary.bossGold    > 0 && <div className="delve-reward-item">💰 +{summary.bossGold} gold (boss kill)</div>}
      {summary.milestoneGold > 0 && <div className="delve-reward-item">🏅 +{summary.milestoneGold} gold (milestone)</div>}
      {summary.shrineGold !== undefined && summary.shrineGold !== 0 && (
        <div className="delve-reward-item">
          {summary.shrineGold > 0 ? '🛕' : '💸'} {summary.shrineGold > 0 ? '+' : ''}{summary.shrineGold} gold (shrine)
        </div>
      )}
      {summary.bossXp > 0 && <div className="delve-reward-item">✨ +{summary.bossXp} XP (boss clear)</div>}
      {summary.forgeTokens > 0 && <div className="delve-reward-item">🔧 +{summary.forgeTokens} Forge Token{summary.forgeTokens > 1 ? 's' : ''}</div>}
      {summary.pendingCards && summary.pendingCards.length > 0 && (
        <div className="delve-reward-item">🃏 {summary.pendingCards.length} card{summary.pendingCards.length > 1 ? 's' : ''} added to your deck!</div>
      )}
      {summary.bossItems && summary.bossItems.length > 0 && (
        <div className="delve-reward-item">⚔️ {summary.bossItems.length} legendary item{summary.bossItems.length > 1 ? 's' : ''} found!</div>
      )}
    </div>
    {summary.bossItems && summary.bossItems.length > 0 && (
      <div style={{ margin: '0.5rem 0', padding: '0.5rem', background: 'rgba(0,0,0,0.25)', borderRadius: '6px' }}>
        {summary.bossItems.map((item, i) => (
          <div key={i} style={{ padding: '0.25rem 0', fontSize: '0.88rem' }}>
            <span className={`rarity-${item.rarity || 'common'}`} style={{ fontWeight: 'bold' }}>{item.name}</span>
            {item.description && <span style={{ color: '#aaa', marginLeft: '0.4rem', fontSize: '0.8rem' }}>— {item.description}</span>}
          </div>
        ))}
      </div>
    )}
    <button className="delve-btn delve-btn-primary" onClick={onClose}>Claim &amp; Exit</button>
  </div>
);

// ── Run Abandoned Screen ───────────────────────────────────────
const ScreenRunAbandoned = ({ summary, onClose }) => (
  <div className="delve-section">
    <div className="delve-event-art">🏳</div>
    <h3 className="delve-section-title">Run Abandoned</h3>
    <p className="delve-text">{summary.message}</p>
    {summary.milestoneGold > 0 && (
      <div className="delve-reward-item">💰 +{summary.milestoneGold} gold kept (milestones)</div>
    )}
    <button className="delve-btn delve-btn-primary" onClick={onClose}>Exit</button>
  </div>
);

export default DelveModal;
